<template>
  <Combobox v-model="state.selected">
    <div class="rc-combobox position-relative">
      <div class="input-group">
        <ComboboxInput
          :class="`form-control form-control-${size} mt-0`"
          :displayValue="(opt) => opt.name"
          v-bind="$attrs"
          :disabled="disabled"
          autocomplete="off"
          @change="state.query = $event.target.value"
          @input="debounceFetch"
        />
        <ComboboxButton class="btn btn-outline-secondary">
          <RcIcon
            name="chevronDownBold"
            scale=".65"
          />
          <RcIcon
            name="chevronUpBold"
            scale=".65"
          />
        </ComboboxButton>
      </div>

      <Transition name="fade">
        <ComboboxOptions
          class="rc-combobox-options position-absolute bg-white rounded border"
        >
          <li v-if="state.suggestions.length === 0 && state.query !== ''">
            <div class="rc-comboBox-option p-2 px-3 bg-lighter">
              {{ $t("common.no_result_found.title") }}
            </div>
          </li>

          <ComboboxOption
            v-for="opt in state.suggestions"
            v-slot="{ active, selected }"
            :key="opt.uid"
            :value="opt"
          >
            <div
              class="rc-combobox-option"
              :class="{
                'bg-secondary bg-opacity-25': active,
                'bg-secondary bg-opacity-10': selected
              }"
            >
              <slot
                :opt="opt"
                name="option"
              >
                {{ opt.name }}
              </slot>
            </div>
          </ComboboxOption>
        </ComboboxOptions>
      </Transition>
    </div>
  </Combobox>
</template>

<script setup>
import { reactive, watch } from 'vue';
import axios from '@/libvue/api/axios.js';
import { debounce } from 'lodash';
import { i18n } from '#/locales';
import RcIcon from '@/libvue/components/icons/Base.vue';

import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption
} from '@headlessui/vue';

defineOptions({
  inheritAttrs: false
});

const emit = defineEmits(['selected', 'empty', 'init']);

const props = defineProps({
  size: {
    type: String,
    required: false,
    default: 'md'
  },
  apiListOptions: {
    type: Object,
    default: () => ({
      url: '',
      method: 'get',
      params: {},
      filters: null
    }),
    required: false
  },
  apiDetailOptions: {
    type: Object,
    default: () => ({
      url: null
    }),
    required: false
  },
  inputOptions: {
    type: Object,
    default: () => ({
      id: 'sameAslabelFor',
      placeholder: i18n.global.t('catalog.playlist_search'),
      class: 'form-control',
      'aria-invalid': null,
      'aria-describedby': null
    })
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const state = reactive({
  disableInput: true,
  query: '',
  results: [],
  timeout: null,
  selected: {},
  suggestions: [],
  initialShow: false
});

const debounceFetch = debounce(function () {
  fetchResults();
}, 350);

watch(
  () => state.selected,
  (selected) => {
    if (selected.name) {
      state.query = selected.name;
      emit('selected', selected);
    } else {
      emit('selected');
    }
  }
);

const fetchDetail = () => {
  return new Promise((resolve, reject) => {
    return axios
      .get(props.apiDetailOptions.url)
      .then(({ data }) => {
        state.selected = data;
        resolve(data);
      })
      .catch((err) => {
        state.selected = {};

        reject(err);
      });
  });
};

const fetchResults = () => {
  return new Promise((resolve, reject) => {
    const _params =
      props.apiListOptions.method == 'get'
        ? { params: { ...props.apiListOptions.params, search: state.query } }
        : { ...props.apiListOptions.params, search: state.query };

    return axios[props.apiListOptions.method](props.apiListOptions.url, _params)
      .then(({ data }) => {
        let results = data.results;
        if (results.length) {
          // apply filters if filters
          if (props.apiListOptions.filters) {
            results = props.apiListOptions.filters(results);
          }
          state.suggestions = results;

          // if (results.length == 1) {
          //   onSelect({ item: results[0] });
          // }
        } else {
          state.suggestions = [];
        }

        resolve(data);
      })
      .catch((err) => {
        state.selected = {};
        reject(err);
      });
  });
};

const init = () => {
  state.selected = {};
  // fetch detail vs fetch list
  let promises = [fetchResults()];

  // add detail promise if detail options
  if (props.apiDetailOptions.url) {
    promises.push(fetchDetail());
  }

  Promise.all(promises).then((values) => {
    state.initialShow = true;
    state.disableInput = false;
    let results = values[0].results;

    // display empty message if no results
    if (!results.length) {
      emit('empty');
    }

    // apply filters if filters
    if (props.apiListOptions.filters) {
      results = props.apiListOptions.filters(results);
    }

    // detail promise was invoked
    if (promises.length == 2) {
      const detail = values[1];
      const match = results.find((i) => i.uid == detail.uid);

      if (!match) {
        results.unshift(detail);
      }
    }

    state.suggestions = results;

    emit('init', results);
  });
};

init();
</script>

<style lang="scss" scoped>
.rc-combobox {
  .btn-outline-secondary {
    svg {
      color: black;
    }
    &:hover svg {
      color: white;
    }
  }
}
.rc-combobox-options {
  z-index: 1000;
  width: 100%;
  max-height: 12.5rem;
  overflow-y: auto;
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.3);
}
.rc-combobox-option {
  padding: 0.4rem 0.75rem;
  border-bottom: 1px solid $gray-300;
}
</style>
